import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { BollettaViewerDialog } from '../components/BollettaViewerDialog';
import {
  Eye,
  ArrowLeft,
  Edit,
  Save,
  X,
  UserCheck,
  Phone,
  Mail,
  MapPin,
  Calendar,
  User,
  Building,
  FileText,
  Clock,
  MessageSquare,
  Send,
  PlusCircle,
  Download,
  CreditCard,
  Globe,
  Zap,
  Flame,
  Wifi,
  Sparkles,
  Loader2,
  BarChart3
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATI_LEAD = [
  { value: 'nuovo', label: 'Nuovo' },
  { value: 'da_contattare', label: 'Da contattare' },
  { value: 'contattato', label: 'Contattato' },
  { value: 'interessato', label: 'Interessato' },
  { value: 'documenti_richiesti', label: 'Documenti richiesti' },
  { value: 'in_trattativa', label: 'In trattativa' },
  { value: 'convertito', label: 'Convertito' },
  { value: 'non_interessato', label: 'Non interessato' },
  { value: 'perso', label: 'Perso' },
];

const LeadDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  
  const [lead, setLead] = useState(null);
  const [users, setUsers] = useState([]);
  const [notes, setNotes] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [newNote, setNewNote] = useState('');
  const [quotazioneBolletta, setQuotazioneBolletta] = useState(null);
  const [bollettePortale, setBollettePortale] = useState([]);
  const [bollettaAperta, setBollettaAperta] = useState(null);
  const [preparandoPreventivo, setPreparandoPreventivo] = useState(false);
  const [analizzandoBolletta, setAnalizzandoBolletta] = useState(null); // bolletta_id in corso

  useEffect(() => {
    fetchLead();
    fetchUsers();
    fetchNotes();
    fetchActivities();
  }, [id]);

  const fetchLead = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/leads/${id}`);
      const leadData = response.data;
      setLead(leadData);
      setEditData(leadData);
      // If lead has quotazione_id but no bolletta_file, check the quotazione
      if (leadData.quotazione_id && !leadData.bolletta_file) {
        try {
          const qRes = await axios.get(`${API_URL}/api/comparatore/quotazioni`);
          const q = qRes.data.find(q => q.id === leadData.quotazione_id);
          if (q?.bolletta_file) setQuotazioneBolletta(q.bolletta_file);
        } catch (e) {}
      }
      // Se lead ha portal_user_id, carico le bollette caricate dal cliente
      if (leadData.portal_user_id) {
        try {
          const bRes = await axios.get(`${API_URL}/api/leads/${id}/bollette-portale`);
          setBollettePortale(bRes.data || []);
        } catch (e) {}
      }
    } catch (error) {
      toast.error('Errore nel caricamento del lead');
      navigate('/leads');
    } finally {
      setLoading(false);
    }
  };

  const handleFaiPreventivo = async () => {
    setPreparandoPreventivo(true);
    try {
      const res = await axios.post(`${API_URL}/api/comparatore/da-lead/${id}`);
      toast.success(`Preventivo creato: ${res.data.num_offerte_confrontate} offerte confrontate`);
      navigate(`/comparatore?quot=${res.data.quotazione_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Impossibile creare il preventivo');
    } finally {
      setPreparandoPreventivo(false);
    }
  };

  const scaricaBollettaPortale = async (b) => {
    try {
      const res = await axios.get(`${API_URL}/api/leads/${id}/bollette-portale/${b.id}/download`, { responseType: 'blob' });
      const ext = (b.file_path || '').split('.').pop() || 'pdf';
      const a = document.createElement('a');
      a.href = URL.createObjectURL(res.data);
      a.download = `Bolletta_${(b.fornitura || '').toUpperCase()}_${b.fornitore || 'cliente'}.${ext}`.replace(/[^\w.-]+/g, '_');
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) {
      toast.error('Impossibile scaricare la bolletta');
    }
  };

  const handleAnalizzaBollettaPortale = async (bollettaId) => {
    setAnalizzandoBolletta(bollettaId);
    try {
      const res = await axios.post(`${API_URL}/api/comparatore/analizza-bolletta-portale/${id}/${bollettaId}`);
      toast.success(`Bolletta analizzata: ${res.data.num_offerte_confrontate} offerte confrontate`);
      navigate(`/comparatore?quot=${res.data.quotazione_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Analisi bolletta fallita');
    } finally {
      setAnalizzandoBolletta(null);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users`);
      setUsers(response.data.filter(u => ['sub_agent', 'admin', 'super_admin'].includes(u.ruolo)));
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchNotes = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/notes/lead/${id}`);
      setNotes(response.data);
    } catch (error) {
      console.error('Error fetching notes:', error);
    }
  };

  const fetchActivities = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/activities/lead/${id}`);
      setActivities(response.data);
    } catch (error) {
      console.error('Error fetching activities:', error);
    }
  };

  const handleSave = async () => {
    try {
      await axios.put(`${API_URL}/api/leads/${id}`, editData);
      toast.success('Lead aggiornato');
      setEditing(false);
      fetchLead();
      fetchActivities();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const handleConvert = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/leads/${id}/convert`);
      toast.success('Lead convertito in cliente');
      navigate(`/clients/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella conversione');
    }
  };

  const handleCreatePratica = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/leads/${id}/crea-pratica`, {});
      toast.success('Pratica creata con successo');
      navigate(`/contracts/${response.data.contract_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione della pratica');
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    
    try {
      await axios.post(`${API_URL}/api/notes`, {
        entita_tipo: 'lead',
        entita_id: id,
        contenuto: newNote
      });
      setNewNote('');
      fetchNotes();
      fetchActivities();
      toast.success('Nota aggiunta');
    } catch (error) {
      toast.error('Errore nell\'aggiunta della nota');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="lead-detail-page">
      <BollettaViewerDialog leadId={id} bolletta={bollettaAperta} onClose={() => setBollettaAperta(null)} />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
          <Button variant="ghost" size="sm" onClick={() => navigate('/leads')} data-testid="back-btn" className="shrink-0">
            <ArrowLeft className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Indietro</span>
          </Button>
          <div className="min-w-0">
            <h1 className="text-lg sm:text-2xl font-bold text-slate-900 truncate">
              {lead?.nome} {lead?.cognome}
            </h1>
            <p className="text-slate-500 text-xs sm:text-sm">Lead #{lead?.id?.slice(0, 8)}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          {editing ? (
            <>
              <Button variant="outline" size="sm" onClick={() => setEditing(false)}>
                <X className="w-4 h-4 mr-2" />
                Annulla
              </Button>
              <Button size="sm" onClick={handleSave} data-testid="save-lead-btn">
                <Save className="w-4 h-4 mr-2" />
                Salva
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="edit-lead-btn">
                <Edit className="w-4 h-4 mr-2" />
                Modifica
              </Button>
              <Button variant="default" size="sm" onClick={handleCreatePratica} data-testid="create-pratica-btn" className="bg-emerald-600 hover:bg-emerald-700">
                <PlusCircle className="w-4 h-4 mr-2" />
                Crea Pratica
              </Button>
              {lead?.stato !== 'convertito' && (
                <Button variant="outline" size="sm" onClick={handleConvert} data-testid="convert-lead-btn">
                  <UserCheck className="w-4 h-4 mr-2" />
                  Converti<span className="hidden sm:inline"> in Cliente</span>
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="w-5 h-5" />
                Informazioni Lead
              </CardTitle>
            </CardHeader>
            <CardContent>
              {editing ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nome</Label>
                    <Input
                      value={editData.nome || ''}
                      onChange={(e) => setEditData({...editData, nome: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Cognome</Label>
                    <Input
                      value={editData.cognome || ''}
                      onChange={(e) => setEditData({...editData, cognome: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Telefono</Label>
                    <Input
                      value={editData.telefono || ''}
                      onChange={(e) => setEditData({...editData, telefono: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      value={editData.email || ''}
                      onChange={(e) => setEditData({...editData, email: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Codice Fiscale</Label>
                    <Input
                      value={editData.codice_fiscale || ''}
                      onChange={(e) => setEditData({...editData, codice_fiscale: e.target.value.toUpperCase()})}
                      placeholder="RSSMRA85M01H501Z"
                      className="font-mono"
                      maxLength={16}
                      data-testid="edit-cf-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Tipo Cliente</Label>
                    <Select 
                      value={editData.tipo_cliente || 'privato'} 
                      onValueChange={(v) => setEditData({...editData, tipo_cliente: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="privato">Privato</SelectItem>
                        <SelectItem value="domestico_residente">Domestico Residente</SelectItem>
                        <SelectItem value="domestico_non_residente">Domestico Non Residente</SelectItem>
                        <SelectItem value="business">Business</SelectItem>
                        <SelectItem value="condominio">Condominio</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-2 space-y-2">
                    <Label>Indirizzo</Label>
                    <Input
                      value={editData.indirizzo || ''}
                      onChange={(e) => setEditData({...editData, indirizzo: e.target.value})}
                      placeholder="Via Roma 1"
                      data-testid="edit-indirizzo-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Città</Label>
                    <Input
                      value={editData.citta || ''}
                      onChange={(e) => setEditData({...editData, citta: e.target.value})}
                    />
                  </div>
                  <div className="flex gap-3">
                    <div className="space-y-2 w-1/2">
                      <Label>Provincia</Label>
                      <Input
                        value={editData.provincia || ''}
                        onChange={(e) => setEditData({...editData, provincia: e.target.value.toUpperCase()})}
                        maxLength={2}
                        placeholder="MI"
                      />
                    </div>
                    <div className="space-y-2 w-1/2">
                      <Label>CAP</Label>
                      <Input
                        value={editData.cap || ''}
                        onChange={(e) => setEditData({...editData, cap: e.target.value})}
                        maxLength={5}
                        placeholder="20100"
                        data-testid="edit-cap-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Stato</Label>
                    <Select 
                      value={editData.stato} 
                      onValueChange={(v) => setEditData({...editData, stato: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATI_LEAD.map(s => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {hasRole(['super_admin', 'admin']) && (
                    <div className="space-y-2">
                      <Label>Assegnato a</Label>
                      <Select 
                        value={editData.assegnato_a || "none"} 
                        onValueChange={(v) => setEditData({...editData, assegnato_a: v === "none" ? "" : v})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleziona" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Non assegnato</SelectItem>
                          {users.map(u => (
                            <SelectItem key={u.id} value={u.id}>{u.nome} {u.cognome}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="sm:col-span-2 space-y-2">
                    <Label>Note</Label>
                    <textarea
                      className="w-full h-24 px-3 py-2 text-sm border rounded-md"
                      value={editData.note || ''}
                      onChange={(e) => setEditData({...editData, note: e.target.value})}
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <Phone className="w-4 h-4 text-slate-400" />
                      <div>
                        <p className="text-xs text-slate-500">Telefono</p>
                        <p className="font-medium">{lead?.telefono}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Mail className="w-4 h-4 text-slate-400" />
                      <div>
                        <p className="text-xs text-slate-500">Email</p>
                        <p className="font-medium">{lead?.email || '-'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <MapPin className="w-4 h-4 text-slate-400" />
                      <div>
                        <p className="text-xs text-slate-500">Indirizzo</p>
                        <p className="font-medium">
                          {lead?.indirizzo || '-'}
                          {lead?.citta && `, ${lead.citta}`}
                          {lead?.provincia && ` (${lead.provincia})`}
                          {lead?.cap && ` - ${lead.cap}`}
                        </p>
                      </div>
                    </div>
                    {lead?.codice_fiscale && (
                      <div className="flex items-center gap-3">
                        <CreditCard className="w-4 h-4 text-slate-400" />
                        <div>
                          <p className="text-xs text-slate-500">Codice Fiscale</p>
                          <p className="font-medium font-mono">{lead.codice_fiscale}</p>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <Building className="w-4 h-4 text-slate-400" />
                      <div>
                        <p className="text-xs text-slate-500">Tipo Cliente</p>
                        <p className="font-medium capitalize">{lead?.tipo_cliente?.replace('_', ' ')}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <FileText className="w-4 h-4 text-slate-400" />
                      <div>
                        <p className="text-xs text-slate-500">Interesse</p>
                        <p className="font-medium capitalize">{lead?.interesse}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <User className="w-4 h-4 text-slate-400" />
                      <div>
                        <p className="text-xs text-slate-500">Assegnato a</p>
                        <p className="font-medium">{lead?.assegnato_a_nome || 'Non assegnato'}</p>
                      </div>
                    </div>
                  </div>
                  {(lead?.bolletta_file || quotazioneBolletta) && (
                    <div className="sm:col-span-2 pt-4 border-t">
                      <a
                        href={`${API_URL}/api/uploads/${lead.bolletta_file || quotazioneBolletta}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-sky-50 hover:bg-sky-100 text-sky-700 rounded-lg border border-sky-200 transition-colors text-sm font-medium"
                        data-testid="download-bolletta-btn"
                      >
                        <Download className="w-4 h-4" />
                        Visualizza Bolletta Originale
                      </a>
                    </div>
                  )}
                  {lead?.note && (
                    <div className="sm:col-span-2 pt-4 border-t">
                      <p className="text-xs text-slate-500 mb-1">Note</p>
                      <p className="text-sm text-slate-700">{lead.note}</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* ==== BOX RICHIESTA DAL WEB / PORTALE ==== */}
          {(lead?.landing_data || lead?.portal_user_id || lead?.provenienza === 'sito_web') && (
            <Card className="border-2 border-sky-200 bg-white shadow-sm" data-testid="box-richiesta-web">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Globe className="w-5 h-5 text-sky-600" />
                  Richiesta dal {lead?.portal_user_id ? 'Portale Clienti' : 'Sito Web'}
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${lead?.portal_user_id ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' : 'bg-sky-100 text-sky-700 border border-sky-200'}`}>
                    {lead?.portal_user_id ? 'Portale' : 'Dal Web'}
                  </span>
                </CardTitle>
                <p className="text-xs text-slate-500 mt-1">
                  {lead?.portal_user_id
                    ? 'Dati e bollette caricati dal cliente nella sua Area Clienti. Usa i bottoni qui sotto per preparare il preventivo nel Comparatore.'
                    : 'Dati inseriti dal cliente nel preventivatore pubblico. Usa i bottoni qui sotto per preparare il preventivo nel Comparatore.'}
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Dati wizard */}
                {lead?.landing_data && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {(() => {
                      const ld = lead.landing_data;
                      const srv = (ld.servizio || lead.interesse || '').toLowerCase();
                      const Icon = srv === 'gas' ? Flame : srv === 'fibra' ? Wifi : Zap;
                      const items = [
                        { label: 'Servizio', value: (ld.servizio || lead.interesse || '-').toUpperCase(), icon: <Icon className="w-3.5 h-3.5" /> },
                        { label: 'Tipo utenza', value: ld.tipo_utenza || '-' },
                        { label: 'CAP', value: ld.cap || lead.cap || '-' },
                        { label: 'Comune', value: ld.comune || lead.citta || '-' },
                      ];
                      if (srv === 'luce' || srv === 'elettricita') {
                        if (ld.consumo_annuo_kwh) items.push({ label: 'Consumo', value: `${ld.consumo_annuo_kwh} kWh/anno` });
                        if (ld.potenza_kw) items.push({ label: 'Potenza', value: `${ld.potenza_kw} kW` });
                        if (ld.n_persone) items.push({ label: 'Persone', value: `${ld.n_persone}` });
                      } else if (srv === 'gas') {
                        if (ld.consumo_annuo_smc) items.push({ label: 'Consumo', value: `${ld.consumo_annuo_smc} Smc/anno` });
                        if (ld.n_fornelli) items.push({ label: 'Fornelli', value: `${ld.n_fornelli}` });
                        if (ld.mq) items.push({ label: 'Mq', value: `${ld.mq}` });
                      }
                      if (ld.operatore_attuale) items.push({ label: 'Operatore attuale', value: ld.operatore_attuale });
                      if (ld.offerta_scelta) items.push({ label: 'Offerta scelta', value: ld.offerta_scelta + (ld.offerta_prezzo ? ` (${ld.offerta_prezzo})` : '') });
                      return items.map((it, i) => (
                        <div key={i} className="bg-white rounded-lg p-2.5 border border-slate-200">
                          <p className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">{it.icon}{it.label}</p>
                          <p className="text-sm font-semibold text-slate-900 mt-0.5 truncate" title={String(it.value)}>{it.value}</p>
                        </div>
                      ));
                    })()}
                  </div>
                )}

                {/* Bollette caricate dal portale */}
                {bollettePortale.length > 0 && (
                  <div className="pt-3 border-t border-slate-200">
                    <p className="text-xs uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1">
                      <FileText className="w-3.5 h-3.5" />
                      Bollette caricate dal cliente ({bollettePortale.length})
                    </p>
                    <div className="space-y-2">
                      {bollettePortale.map((b) => (
                        <div key={b.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 bg-white rounded-lg p-2.5 border border-slate-200">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className={`shrink-0 w-8 h-8 rounded flex items-center justify-center ${b.fornitura === 'gas' ? 'bg-orange-100 text-orange-600' : 'bg-sky-100 text-sky-600'}`}>
                              {b.fornitura === 'gas' ? <Flame className="w-4 h-4" /> : <Zap className="w-4 h-4" />}
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-slate-900 truncate">
                                {b.fornitura?.toUpperCase()} · {b.fornitore || 'Fornitore N/D'} · {b.importo != null ? `${Number(b.importo).toFixed(2)} EUR` : 'Importo N/D'}
                              </p>
                              <p className="text-[11px] text-slate-500">
                                {b.periodo_fatturazione || (b.periodo_from ? `${b.periodo_from} - ${b.periodo_to || '?'}` : 'Periodo N/D')}
                                {b.scadenza ? ` · Scade ${b.scadenza}` : ''}
                              </p>
                              {(b.costo_unitario || b.quota_fissa_mensile || b.consumo_periodo || b.storico_consumi?.length > 0) && (
                                <p className="text-[11px] text-sky-700 dark:text-sky-300 mt-0.5" data-testid={`dettaglio-bolletta-portale-${b.id}`}>
                                  {b.costo_unitario ? `Prezzo ${b.costo_unitario} ${b.fornitura === 'gas' ? '€/Smc' : '€/kWh'}` : ''}
                                  {b.quota_fissa_mensile ? ` · Fisso ${Number(b.quota_fissa_mensile).toFixed(2)} €/mese` : ''}
                                  {b.consumo_periodo ? ` · Consumo periodo ${b.consumo_periodo} ${b.fornitura === 'gas' ? 'Smc' : 'kWh'}` : ''}
                                  {b.storico_consumi?.length > 0 ? ` · Storico grafico: ${b.storico_consumi.length} mesi = ${b.storico_consumi.reduce((t, x) => t + (Number(x.consumo) || 0), 0)} ${b.fornitura === 'gas' ? 'Smc' : 'kWh'}` : ''}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-wrap items-center gap-1 shrink-0">
                            {b.file_path && (
                              <Button
                                size="sm" variant="outline" className="h-7 px-2 text-[11px]"
                                onClick={() => setBollettaAperta(b)}
                                data-testid={`vedi-bolletta-portale-${b.id}`}
                              >
                                <Eye className="w-3 h-3 mr-1" />Vedi
                              </Button>
                            )}
                            {b.file_path && (
                              <Button
                                size="sm" variant="outline" className="h-7 px-2 text-[11px]"
                                onClick={() => scaricaBollettaPortale(b)}
                                data-testid={`download-bolletta-portale-${b.id}`}
                              >
                                <Download className="w-3 h-3 mr-1" />Scarica
                              </Button>
                            )}
                            {b.file_path && (
                              <Button
                                size="sm" variant="outline" className="h-7 px-2 text-[11px]"
                                onClick={() => handleAnalizzaBollettaPortale(b.id)}
                                disabled={analizzandoBolletta === b.id}
                                data-testid={`analizza-bolletta-portale-${b.id}`}
                              >
                                {analizzandoBolletta === b.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Sparkles className="w-3 h-3 mr-1" />Analizza AI</>}
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Bottoni azione */}
                <div className="pt-3 border-t border-slate-200 flex flex-wrap gap-2">
                  <Button
                    onClick={handleFaiPreventivo}
                    disabled={preparandoPreventivo}
                    className="bg-sky-600 hover:bg-sky-700 text-white"
                    data-testid="btn-fai-preventivo-lead"
                  >
                    {preparandoPreventivo ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BarChart3 className="w-4 h-4 mr-2" />}
                    Apri Comparatore (precompilato)
                  </Button>
                  {bollettePortale.some(b => b.file_path) && (
                    <span className="text-xs text-slate-500 self-center ml-1">
                      · oppure usa "Analizza AI" sulla bolletta sopra
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Tabs for Notes and Activities */}
          <Tabs defaultValue="notes">
            <TabsList>
              <TabsTrigger value="notes">Note ({notes.length})</TabsTrigger>
              <TabsTrigger value="activities">Storico ({activities.length})</TabsTrigger>
            </TabsList>
            
            <TabsContent value="notes" className="mt-4">
              <Card>
                <CardContent className="p-4 space-y-4">
                  {/* Add Note */}
                  <div className="flex gap-2">
                    <Input
                      placeholder="Aggiungi una nota..."
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddNote()}
                      data-testid="new-note-input"
                    />
                    <Button onClick={handleAddNote} data-testid="add-note-btn">
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {/* Notes List */}
                  <div className="space-y-3">
                    {notes.length === 0 ? (
                      <p className="text-center text-slate-500 py-8">Nessuna nota</p>
                    ) : (
                      notes.map((note) => (
                        <div key={note.id} className="p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-sm">{note.autore_nome}</span>
                            <span className="text-xs text-slate-500">{formatDate(note.created_at)}</span>
                          </div>
                          <p className="text-sm text-slate-700">{note.contenuto}</p>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="activities" className="mt-4">
              <Card>
                <CardContent className="p-4">
                  <div className="space-y-3">
                    {activities.length === 0 ? (
                      <p className="text-center text-slate-500 py-8">Nessuna attività</p>
                    ) : (
                      activities.map((activity) => (
                        <div key={activity.id} className="flex items-start gap-3 p-3 border-l-2 border-slate-200">
                          <Clock className="w-4 h-4 text-slate-400 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-slate-900">{activity.azione}</p>
                            {activity.dettagli && (
                              <p className="text-sm text-slate-500">{activity.dettagli}</p>
                            )}
                            <p className="text-xs text-slate-400 mt-1">
                              {activity.utente_nome} • {formatDate(activity.created_at)}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Stato</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-xs text-slate-500 mb-1">Stato attuale</p>
                <span className={`inline-flex px-3 py-1.5 rounded-full text-sm font-medium ${
                  lead?.stato === 'convertito' ? 'bg-green-100 text-green-700' :
                  lead?.stato === 'perso' || lead?.stato === 'non_interessato' ? 'bg-red-100 text-red-700' :
                  'bg-sky-100 text-sky-700'
                }`}>
                  {STATI_LEAD.find(s => s.value === lead?.stato)?.label}
                </span>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Priorità</p>
                <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${
                  lead?.priorita === 'alta' ? 'bg-red-100 text-red-600' :
                  lead?.priorita === 'media' ? 'bg-amber-100 text-amber-600' :
                  'bg-slate-100 text-slate-600'
                }`}>
                  {lead?.priorita?.toUpperCase()}
                </span>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Provenienza</p>
                <p className="font-medium capitalize">{lead?.provenienza?.replace('_', ' ')}</p>
              </div>
              {lead?.pod && (
                <div>
                  <p className="text-xs text-slate-500 mb-1">POD</p>
                  <p className="font-mono text-sm">{lead.pod}</p>
                </div>
              )}
              {lead?.pdr && (
                <div>
                  <p className="text-xs text-slate-500 mb-1">PDR</p>
                  <p className="font-mono text-sm">{lead.pdr}</p>
                </div>
              )}
              {lead?.scadenza_condizioni && (
                <div>
                  <p className="text-xs text-slate-500 mb-1">Scadenza Condizioni</p>
                  <p className="text-sm font-medium text-amber-600">{new Date(lead.scadenza_condizioni).toLocaleDateString('it-IT')}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Dates Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Date</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-xs text-slate-500">Creato il</p>
                <p className="text-sm font-medium">{formatDate(lead?.created_at)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Ultimo aggiornamento</p>
                <p className="text-sm font-medium">{formatDate(lead?.updated_at)}</p>
              </div>
              {lead?.ultimo_contatto && (
                <div>
                  <p className="text-xs text-slate-500">Ultimo contatto</p>
                  <p className="text-sm font-medium">{formatDate(lead?.ultimo_contatto)}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default LeadDetail;
